import { useCallback, useMemo, useState, type ReactNode } from "react";

import type { ProgramDetail } from "@/lib/scoring/api";
import { normalize, runsEverywhere, runsIn } from "@/lib/scoring/layout";
import type { AnyNode, NodeKind, ScoringProgram, Variant } from "@/lib/scoring/types";

import editor from "./Editor.module.css";
import { NodeEditor } from "./NodeEditor";
import { ProgramCanvas } from "./ProgramCanvas";

/**
 * The program viewer.
 *
 * It used to be the builder. Behaviour lives in the omnium-flows plugin now and
 * every write route answers 410, so what is left is the half that still means
 * something: the picture of the program, the node you selected, and the Python
 * behind it. Nothing here can change a program — that is a change in the plugin
 * and a republish.
 *
 * Every workflow draws here: a sport's scoring, and the ones no sport owns —
 * Results and Games operations. Those have no sport, so they have no formats to
 * filter by and no masters; their commands, rules and actions draw the same.
 */
export function ProgramEditor({
  initial,
  variants = [],
  roles = [],
  back,
  actions,
}: {
  initial: ProgramDetail;
  /** This sport's kinds of fixture and configs — what `appliesTo` may hold. */
  variants?: Variant[];
  /** Role codes an actor may be mapped to, from `role_dictionary`. */
  roles?: string[];
  /** Where the back arrow goes. */
  back?: { href: string; label: string };
  /** More links in the top bar, e.g. to the workflow's apps. */
  actions?: ReactNode;
}) {
  const [detail] = useState(initial);
  const [showFrozen, setShowFrozen] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Normalized rather than spread: an empty draft carries no sections at all,
  // and the canvas reads every one of them.
  const program: ScoringProgram = useMemo(() => normalize(detail.document), [detail]);

  const editorFor = useCallback(
    (kind: NodeKind, node: AnyNode) => (
      // Keyed by node: selecting another node remounts rather than reusing the
      // previous one's state.
      <NodeEditor
        key={node.id}
        programId={detail.id}
        kind={kind}
        node={node}
        scope={SAMPLE_SCOPE}
        roles={roles}
        taken={allNodes(program)
          .filter((other) => other.id !== node.id)
          .map((other) => other.code)}
        // Only asked for a component, but computed unconditionally: it is one
        // pass over the commands, and branching here would put the decision
        // about what a component is in two places.
        runsIn={runsIn(program, node.id).map((command) => ({
          id: command.id,
          code: command.code,
        }))}
        everywhere={runsEverywhere(kind)}
        onOpenCommand={setSelectedId}
      />
    ),
    [detail.id, roles, program],
  );

  const toolbar = (
    <div className={editor.bar}>
      <span className={editor.frozenHint}>Read-only — generated from the flows plugin</span>
      {actions}
    </div>
  );

  /**
   * Why nothing on this screen can be changed.
   *
   * Closable, because it answers a question you only ask once and then it is
   * just a strip eating the canvas.
   */
  const frozen = showFrozen && (
    <div className={editor.frozenBar}>
      <span>
        <strong>Version {detail.version} is read-only</strong> — behaviour lives in the
        omnium-flows plugin. Change the workflow there and republish; nothing on this screen
        can be edited.
      </span>
      <button
        className={editor.frozenClose}
        type="button"
        aria-label="Close"
        onClick={() => setShowFrozen(false)}
      >
        ×
      </button>
    </div>
  );

  return (
    <ProgramCanvas
      program={program}
      programId={detail.id}
      title={detail.name ?? detail.workflowName ?? undefined}
      sport={detail.sportCode || null}
      version={detail.version}
      back={back}
      toolbar={toolbar}
      banner={frozen}
      editorFor={editorFor}
      selectedId={selectedId}
      variants={variants}
      onSelect={(_kind, node) => setSelectedId(node.id)}
    />
  );
}

function allNodes(program: ScoringProgram): AnyNode[] {
  return [
    ...program.commands,
    ...program.validations,
    ...program.computes,
    ...program.triggers,
    ...(program.context ? [program.context] : []),
  ];
}


/**
 * What an expression previews against, and what Run passes a function.
 *
 * A placeholder until the run bar can pick a real fixture: the shape is right,
 * so an author sees `input.runs` resolve rather than a blank, and swapping in a
 * recorded fixture later changes only this value.
 */
const SAMPLE_SCOPE: Record<string, unknown> = {
  input: { batter: "a1", bowler: "b1", runs: 4, extra: null, wicket: null },
  ctx: {
    fixture: { format: { overs: 20, ballsPerOver: 6, maxOversPerBowler: 4 } },
    derived: { striker: "a1", outBatters: [], legalBalls: 3, runs: 12, wickets: 0 },
    log: [],
    stats: {},
  },
  log: [],
  derived: { striker: "a1", outBatters: [], legalBalls: 3, runs: 12, wickets: 0 },
  fixture: { format: { overs: 20, ballsPerOver: 6, maxOversPerBowler: 4 }, sides: ["a", "b"] },
};
